import { useState, useEffect, Dispatch, SetStateAction, useContext } from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  makeStyles,
  Checkbox,
  Body2,
} from "@fluentui/react-components";
import {
  AppButton,
  AppUserSelect,
  AppDateSelect,
  AppDivider,
  AppTimeInput,
  AppSelect,
  AppTextArea,
} from "../common";
import { useForm } from "react-hook-form";
import usersService from "../../services/users.service";
import dataTimeService from "../../services/dataTime.service";
import { IUserSelectable } from "../../services/types/users";
import { documentTypeEnum, overtimeTypeEnum, userRoleEnum } from "../../utils/enums";
import appErrorToast from "../common/AppErrorToast";
import UserContext from "../../modules/AppRouter/UserContext";
import OvertimeAssigments from "../../services/overtimeAssigments.service";
import { IOvertimeAssigment } from "../../services/types/overtimeAssigments";
import { useParams } from "react-router-dom";
import { ContractTypeApi, isPayoutOnlyContract } from "../../utils/contractType";
import { normalizePayoutRate } from "../../utils/payoutRate";

export type ModifyAssignmentEditScope = "full" | "commissionTypeAfterRealized";
const PAYOUT_TYPE = "Wypłata" as const;
const TIME_TO_COLLECT_TYPE = "Do odbioru" as const;

interface IFormData {
  employeeId: string;
  employeeContractType?: ContractTypeApi | null;
  documentType?: string;
  assigmentDate: Date;
  duration: string;
  type: typeof PAYOUT_TYPE | typeof TIME_TO_COLLECT_TYPE | null;
  applicationType: string;
  assignmentDescription: string;
  generateApplication: boolean;
  payoutRate: number;
  changeJustification?: string;
}

interface IDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  formData: IFormData;
  refresh: () => void;
  editScope?: ModifyAssignmentEditScope;
}

const useStyles = makeStyles({
  dialogContainer: {
    maxWidth: "450px",
  },
  readOnlyBlock: {
    marginBottom: "8px",
  },
});

const ModifyAssignmentDialog: React.FC<IDialogProps> = ({
  open,
  setOpen,
  formData,
  refresh,
  editScope = "full",
}) => {
  const styles = useStyles();
  const { id } = useParams<{ id: string }>();
  const { register, handleSubmit, reset, watch, setValue, control, formState: { errors } } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { userData } = useContext(UserContext);
  const applicationTypeFieldValue = watch("applicationType");
  const typeFieldValue = watch("type");
  const selectedEmployeeId = watch("employeeId");
  const [userOptions, setUserOptions] = useState<IUserSelectable[]>([]);
  const isCommissionAfterRealized = editScope === "commissionTypeAfterRealized";
  const canUseCommissionAfterRealizedFlow = formData.documentType === documentTypeEnum.Commision;
  const selectedEmployee = userOptions.find(
    (x) => x.id === (isCommissionAfterRealized ? formData.employeeId : selectedEmployeeId)
  );
  const effectiveContractType = selectedEmployee?.contractType ?? formData.employeeContractType ?? null;
  const isPayoutOnlyEmployee =
    userData.data.baseRole === userRoleEnum.Admin
    && isPayoutOnlyContract(effectiveContractType);
  const typeOptions: Array<typeof PAYOUT_TYPE | typeof TIME_TO_COLLECT_TYPE> =
    isPayoutOnlyEmployee ? [PAYOUT_TYPE] : [PAYOUT_TYPE, TIME_TO_COLLECT_TYPE];
  const initialDurationValue = formData.duration.includes(":")
    ? formData.duration
    : dataTimeService.convertFormat(parseInt(formData.duration, 10));
  const normalizedInitialPayoutRate = normalizePayoutRate(formData.payoutRate);

  useEffect(() => {
    usersService.selectableUsers().then((users) => {
      if (userData.data.baseRole === userRoleEnum.Employee) {
        users = users.filter((x) => x.id === userData.data.id);
      }
      setUserOptions(users);
    });
  }, [userData.data.baseRole, userData.data.id]);

  const applyFormDataToFields = () => {
    const shouldForcePayoutOnly = isPayoutOnlyEmployee && formData.applicationType === "Overtime";
    const initialType = shouldForcePayoutOnly ? PAYOUT_TYPE : formData.type;

    setValue("assignmentDescription", formData.assignmentDescription);
    setValue("generateApplication", formData.generateApplication);
    setValue(
      "applicationType",
      formData.applicationType === "Leaving" ? "Wyjście" : "Nadgodziny",
    );

    if (userData.data.baseRole === userRoleEnum.Admin) {
      setValue("type", initialType);
    }

    setValue("payoutRate", normalizedInitialPayoutRate);

    setValue("employeeId", formData.employeeId);
    setValue("assigmentDate", formData.assigmentDate);
    setValue("duration", isCommissionAfterRealized ? formData.duration : initialDurationValue);
    if (isCommissionAfterRealized) {
      setValue("changeJustification", "");
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    applyFormDataToFields();
    if (userData?.data?.baseRole === userRoleEnum.Admin && userOptions.length > 0) {
      if (userOptions.find((x) => x.id === formData.employeeId)) {
        setValue("employeeId", formData.employeeId);
      }
    }
  }, [open, formData, userData, userOptions, isCommissionAfterRealized, setValue]);

  useEffect(() => {
    if (!open || !isPayoutOnlyEmployee) {
      return;
    }

    setValue("type", PAYOUT_TYPE);
  }, [open, isPayoutOnlyEmployee, selectedEmployeeId, setValue, isCommissionAfterRealized]);

  async function handleFormSubmit(data: IFormData & { changeJustification?: string }) {
    if (isCommissionAfterRealized && !canUseCommissionAfterRealizedFlow) {
      appErrorToast("Po realizacji typ rozliczenia można zmieniać wyłącznie dla zleceń.");
      return;
    }

    setIsLoading(true);

    const durationMinutes = isCommissionAfterRealized
      ? Number(formData.duration)
      : dataTimeService.convertTimeToMin(data.duration);

    if (!isCommissionAfterRealized && durationMinutes < 1) {
      appErrorToast("Ustaw prawidłową liczbę nadgodzin");
      setIsLoading(false);
      return;
    }

    const employeeId = isCommissionAfterRealized ? formData.employeeId : data.employeeId;
    const assignmentDate = isCommissionAfterRealized ? formData.assigmentDate : data.assigmentDate;
    const assignmentDescription = isCommissionAfterRealized
      ? formData.assignmentDescription
      : data.assignmentDescription;
    const generateApplication = isCommissionAfterRealized
      ? formData.generateApplication
      : data.generateApplication;

    const typeUi = isPayoutOnlyEmployee ? PAYOUT_TYPE : (data.type ?? formData.type);
    const payoutForApi = Number(data.payoutRate ?? formData.payoutRate) || 0;

    const payload: IOvertimeAssigment = {
      employeeId,
      assignmentDate,
      duration: durationMinutes,
      type:
        typeUi === PAYOUT_TYPE
          ? overtimeTypeEnum.TimeToPay
          : overtimeTypeEnum.TimetoCollection,
      assignmentDescription,
      generateApplication,
      payoutRate: payoutForApi,
      asAdmin: localStorage.getItem("workContext") === userRoleEnum.Admin,
    };

    if (isCommissionAfterRealized) {
      const note = data.changeJustification?.trim();
      if (note) {
        payload.changeJustification = note;
      }
    }

    OvertimeAssigments.editAssigment(payload, id)
      .then(() => {
        reset();
        setOpen(false);
        refresh();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const handleApplicationTypeChanges = () => {
    if (applicationTypeFieldValue === "Nadgodziny") {
      setValue("generateApplication", false);
    }
  };

  const dialogTitle = isCommissionAfterRealized
    ? "Zmiana typu rozliczenia (zrealizowane zlecenie)"
    : userData.data.baseRole === userRoleEnum.Admin
      ? "Formularz zlecenia nadgodzin"
      : "Wniosek o nadgodziny/wyjście";

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface className={styles.dialogContainer}>
        <form>
          <DialogBody>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
              <AppDivider />
              {isCommissionAfterRealized && userData.data.baseRole === userRoleEnum.Admin && (
                <>
                  <Body2 className={styles.readOnlyBlock}>
                    Zmieniasz wyłącznie typ (wypłata / do odbioru) i stawkę. Pozostałe dane muszą pozostać
                    zgodne z realizacją.
                  </Body2>
                  <AppDivider />
                </>
              )}
              {!isCommissionAfterRealized && (
                <>
                  <AppUserSelect
                    label="Wybierz pracownika"
                    options={userOptions}
                    formHandler={register("employeeId", {
                      required: true,
                    })}
                  />
                  <AppDivider />
                  <AppDateSelect
                    label="Wybierz datę"
                    formHandler={register("assigmentDate", {
                      required: true,
                    })}
                    defaultValue={dataTimeService.dateToString(formData.assigmentDate)}
                  />
                  <AppDivider />
                  <AppTimeInput
                    type="time"
                    label="Liczba godzin"
                    formHandler={register("duration", {
                      required: { value: true, message: "Liczba godzin jest wymagana" },
                      min: 1,
                    })}
                    control={control}
                    errors={errors}
                    defaultValue={initialDurationValue}
                  />
                </>
              )}
              {userData.data.baseRole === userRoleEnum.Admin && (
                <>
                  {!isCommissionAfterRealized && <AppDivider />}
                  <AppSelect
                    label="Typ"
                    options={typeOptions}
                    formHandler={register("type", { required: true })}
                    disabled={isPayoutOnlyEmployee}
                  />
                  {isPayoutOnlyEmployee && (
                    <>
                      <AppDivider />
                      <Body2>
                        Dla osób na umowie B2B oraz umowie zlecenie, nie ma możliwości
                        zlecenia nadgodzin do odbioru.
                      </Body2>
                    </>
                  )}
                  {typeFieldValue === PAYOUT_TYPE && (
                    <>
                      <AppDivider />
                      <AppSelect
                        label="% wynagrodzenia"
                        options={["100", "150", "200"]}
                        formHandler={register("payoutRate", {
                          required: true,
                        })}
                      />
                    </>
                  )}
                </>
              )}
              {userData.data.baseRole === userRoleEnum.Employee && !isCommissionAfterRealized && (
                <>
                  <AppDivider />
                  <AppSelect
                    label="Typ"
                    options={["Nadgodziny", "Wyjście"]}
                    formHandler={register("applicationType", { required: true })}
                    onClick={handleApplicationTypeChanges}
                    disabled
                  />
                  {applicationTypeFieldValue === "Wyjście" && (
                    <>
                      <AppDivider />
                      <Checkbox
                        label="Odrabiam tego samego dnia"
                        {...register("generateApplication")}
                        checked={watch("generateApplication")}
                        disabled
                      />
                    </>
                  )}
                </>
              )}
              {!isCommissionAfterRealized && (
                <>
                  <AppDivider />
                  <AppTextArea
                    label="Opis"
                    formHandler={register("assignmentDescription", { maxLength: 1000 })}
                  />
                </>
              )}
              {isCommissionAfterRealized && userData.data.baseRole === userRoleEnum.Admin && (
                <>
                  <AppDivider />
                  <AppTextArea
                    label="Uzasadnienie zmiany (opcjonalnie, do audytu)"
                    formHandler={register("changeJustification", { maxLength: 500 })}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <AppButton
                text="Anuluj"
                appearance="secondary"
                onClick={() => {
                  setOpen(false);
                }}
              />
              <AppButton
                text="Zapisz"
                disabled={isLoading}
                appearance="primary"
                onClick={handleSubmit((data) => {
                  handleFormSubmit(data as IFormData);
                })}
              />
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default ModifyAssignmentDialog;
